import React, {useEffect} from 'react'
import clsx from 'clsx'
import {Link, useOutletContext, useRouteLoaderData} from '@remix-run/react'
import type {FunctionComponent, SyntheticEvent} from 'react'
import {getTranslation} from '~/localization/translation'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {useReportWidgetClick, useReportWidgetSeen, useSendPartnerProductClickoutCustom} from '~/hooks/analytic-hooks'
import {KeyFeaturesIcon} from '~/icons/product-page/key-features-icon'
import {BrgIconArrowRightKeyFeatures} from '~/icons/brg-icon-arrow-right-key-fetaures'
import type {Product, RootAndListContext, RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'
import {cssDomains} from '~/utils/constants'

interface KeyFeaturesProps {
  placement: number
  productPageSlug?: string
  showProductPageLinks?: boolean
  affiliateLinkWithClickId: string
  product: Product
  isKeyFeaturesOpen: boolean
  setIsKeyFeaturesOpen: (value: boolean) => void
  handleCardClick: (e: SyntheticEvent) => void
}
const KeyFeatures: FunctionComponent<KeyFeaturesProps> = ({
  placement,
  isKeyFeaturesOpen,
  setIsKeyFeaturesOpen,
  productPageSlug,
  showProductPageLinks = true,
  product,
  affiliateLinkWithClickId,
}) => {
  const {slug} = useOutletContext<RootAndListContext>()
  const {domain, locale, language, featureFlags, forcedLanguage} = useRouteLoaderData('root') as RootLoaderResponse
  const [productUrl, setProductUrl] = React.useState<string | undefined>(undefined)
  const translations = getTranslation(language)
  const reportClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickoutCustom()
  const {styles: stylesBydomain, domainName} = getDomainConfig(domain, locale)

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'engaging-key-features',
    widget_place: `card-${placement}`,
  })
  const [readFullReviewRef] = useReportWidgetSeen({
    widget_name: 'read-full-review-cta',
    widget_place: `card-${placement}`,
  })

  function handleSeeFullReviewClick() {
    reportClick({
      widget_name: 'read-full-review-cta',
      widget_place: `card-${placement}`,
    })
    reportClickout({
      index: placement,
      item_list_id: slug || '',
      click_element_name: 'read-full-review-cta',
      partner_item_brand: product?.brand || '',
      partner_item_id: product?.asin || '',
      item_name: product?.name || '',
      item_brand: product?.brand || '',
      click_id: product?.affiliateLink?.clickId,
      affiliation: product?.affiliateLink?.type,
      is_dws: product?.isSellerProduct,
      location_id: locale,
      traffic: 'unknown',
      gclid: undefined,
      sys_id: undefined,
      target: affiliateLinkWithClickId,
    })
    setTimeout(() => window.open(affiliateLinkWithClickId, '_self', 'noopener,noreferrer'), 1000)
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname
      const newPath = `${currentPath}/${productPageSlug}`
      const newTabUrl = `${window.location.origin}${newPath}`
      setProductUrl(newTabUrl)
    }
  }, [productPageSlug])

  const showKeyFeatures =
    product?.keyFeatures && product?.keyFeatures?.length > 0 && forcedLanguage?.toLowerCase() !== 'es'

  return showKeyFeatures ? (
    <div ref={widgetRef}>
      <div
        className={clsx(
          'overflow-hidden border-0 border-t border-solid py-4 transition-all duration-200 ease-in-out desktop:px-8 tablet:mx-6 tablet:py-2',
          isKeyFeaturesOpen ? '!max-h-[500px]' : '!max-h-[65px] tablet:!max-h-[75px]',
        )}
      >
        <div className="mb-1 flex flex-row items-center gap-2">
          {featureFlags?.useReorganizedCardLayout ? <KeyFeaturesIcon /> : null}
          <p className="text-base font-semibold">
            {featureFlags?.useReorganizedCardLayout
              ? translations.ProductCard.keyFeatures
              : translations.ProductCard.productSpecsTitle}
          </p>
        </div>
        <ul className="list-outside px-1" data-testid="key-feature-content">
          {product?.keyFeatures?.slice(0, 5)?.map(({feature, description}, index) => (
            <li key={`${feature}-${index}`} className="mb-1 flex flex-row items-start">
              <p className="mb-auto text-sm font-medium">{index + 1}.</p>
              <p
                className={clsx(
                  'ml-1 text-sm font-light [&>b]:font-semibold',
                  !isKeyFeaturesOpen && 'line-clamp-1 pr-44 tablet:line-clamp-2 tablet:pr-0',
                )}
              >
                <b>{feature}</b>: {description}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="my-3 flex flex-row justify-between px-8" ref={readFullReviewRef}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsKeyFeaturesOpen(!isKeyFeaturesOpen)
            reportClick({
              widget_name: 'expand-key-features-button',
              widget_place: `card-${placement}`,
              trigger_type: isKeyFeaturesOpen ? 'close' : 'open',
            })
          }}
          className={clsx(
            'flex h-8 flex-row items-center gap-2 stroke-[0.01em] text-sm font-medium capitalize',
            stylesBydomain.LISTPAGE_BUTTON_LINK,
            'listpage_button_secondary',
          )}
        >
          {translations.ProductCard.viewMore(isKeyFeaturesOpen)}
          <IconChevronRightThick
            className={clsx(
              'size-4 transition-all duration-200 ease-in-out',
              isKeyFeaturesOpen ? '-rotate-90' : 'rotate-90',
              stylesBydomain.LISTPAGE_BUTTON_ICON_FILL,
              'listpage_button_icon_fill',
            )}
          />
        </button>
        {showProductPageLinks && featureFlags?.useProductFeaturesPage && productPageSlug ? (
          <div
            className={clsx(
              'relative ml-auto flex flex-row items-center gap-2 text-[18px] font-medium tablet:text-sm',
              stylesBydomain.LISTPAGE_BUTTON_LINK,
              'listpage_button_link',
            )}
          >
            <a
              className="absolute inset-0"
              href={productUrl}
              target="_blank"
              aria-label={translations.ProductCard.readFullReview}
              rel="noreferrer"
              onClick={handleSeeFullReviewClick}
            ></a>
            <p>{translations.ProductCard.readFullReview}</p>
            <BrgIconArrowRightKeyFeatures
              className={clsx(stylesBydomain.LISTPAGE_BUTTON_ICON_FILL, 'listpage_button_icon_fill')}
            />
          </div>
        ) : null}
        {showProductPageLinks && cssDomains.includes(domainName) && product?.productStorePageSlug ? (
          <Link
            to={`/pp/${slug}/${product?.productStorePageSlug}`}
            prefetch="intent"
            aria-label={translations.ProductCard.readFullReview}
            onClick={() => {
              reportClick({
                widget_name: 'read-full-review-store-page-link',
                widget_place: `card-${placement}`,
              })
            }}
            className={clsx(
              'relative flex flex-row items-center gap-2 text-[18px] font-medium tablet:text-sm',
              stylesBydomain.LISTPAGE_BUTTON_LINK,
              'listpage_button_link',
            )}
          >
            {translations.ProductCard.readFullReview}
            <BrgIconArrowRightKeyFeatures
              className={clsx(stylesBydomain.LISTPAGE_BUTTON_ICON_FILL, 'listpage_button_icon_fill')}
            />
          </Link>
        ) : null}
      </div>
    </div>
  ) : null
}

export default KeyFeatures
